import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MousePointerClick, Database } from 'lucide-react';
import type { HelpEntry } from '../../data/helpContent';

interface HelpModalProps {
  entry: HelpEntry | null;
  onClose: () => void;
}

export function HelpModal({ entry, onClose }: HelpModalProps) {
  useEffect(() => {
    if (!entry) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [entry, onClose]);

  return createPortal(
    <AnimatePresence>
      {entry && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            className="absolute inset-0 backdrop-blur-sm"
            style={{ backgroundColor: 'var(--color-backdrop)' }}
            onClick={onClose}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-6 z-10"
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-muted hover:text-secondary transition-colors" aria-label="Close">
              <X size={18} />
            </button>
            <h3 className="text-base font-bold text-primary pr-8 mb-3">{entry.title}</h3>
            <p className="text-sm text-secondary leading-relaxed mb-4">{entry.description}</p>
            {entry.interactiveNote && (
              <div className="flex gap-2.5 bg-chart-teal/10 border border-chart-teal/20 rounded-lg px-3.5 py-2.5 mb-4">
                <MousePointerClick size={16} className="text-chart-teal shrink-0 mt-0.5" />
                <p className="text-xs text-chart-teal leading-relaxed">{entry.interactiveNote}</p>
              </div>
            )}
            {entry.source && (
              <div className="flex items-center gap-1.5 text-detail text-muted">
                <Database size={11} />
                <span>{entry.source}</span>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
